import moment from "moment";
import React, { useState } from "react";
import NumberFormat from "react-number-format";
import { DYNAMIC_FIELD_TYPES } from "../../../common/constants";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { editorConfiguration } from "../../../../utils/ckEditorUtils";
import Portal from "../../../common/components/Portal";
import { RichEditorPopup } from "../../../common/components/formFields/RichEditorPopup";
import CalendarIcon from "../../../../assets/img/Group7.svg";
import ReactTooltip from "react-tooltip";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import { thousandsSeparators } from "../../../../config/utils";
import { getOptionColor, readableTextColor } from "../../../../utils/optionColors";
import "../../../common/components/table/dynamicField.css";

export const renderHighlightedLabel = (labelValue, fieldSearch = "") => {
    const query = (fieldSearch ?? "").toString().trim();
    const labelText = labelValue ?? "";
    if (!query || typeof labelText !== "string") return labelText;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return labelText.split(new RegExp(`(${escaped})`, "ig")).map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
            <span key={`${part}-${index}`} className="asset-field-search-label">
                {part}
            </span>
        ) : (
            part
        )
    );
};

export const Field = ({ elem, data, isDynamic }) => {
    const value = data[elem?.key];
    return (
        <div class="codeOtr">
            <h4>{elem.label}</h4>
            <h3>
                {elem.type === "number" ? (
                    <NumberFormat value={value || 0} thousandSeparator={true} displayType={"text"} prefix={elem.isCost ? "$ " : ""} />
                ) : (
                    <>{value || "-"}</>
                )}
            </h3>
        </div>
    );
};

export const DynamicField = ({
    elem,
    data,
    flat_value = "",
    invalidFields,
    dynamicClassName = "",
    hasTooltip = false,
    isMR = false,
    fieldSearch = ""
}) => {
    const [showRichTextPopup, setShowRichTextPopup] = useState({ label: "", key: "", show: false });
    const value = flat_value || data?.[elem?.field_name]?.value;
    const isInvalid = invalidFields?.dynamic_fields?.[elem?.field_name]?.value;
    const { prefix = "", suffix = "", thousand_separator = false, decimal_scale = 0, show_lease_chart } = elem?.rules;
    const displayValue = thousand_separator && value !== "" && value !== null && value !== undefined && !isNaN(value)
        ? thousandsSeparators(value)
        : value;
    const optionColor = displayValue ? getOptionColor(elem?.optionColors, value) : undefined;
    const renderRichTextPopup = () => {
        const { label, key, show } = showRichTextPopup;
        if (!show) return null;
        return (
            <Portal
                body={
                    <RichEditorPopup
                        value={value}
                        label={label}
                        key={key}
                        onCancel={() => setShowRichTextPopup({ ...showRichTextPopup, show: false })}
                        isReadOnly
                    />
                }
                onCancel={() => setShowRichTextPopup({ ...showRichTextPopup, show: false })}
            />
        );
    };

    return (
        <div class={`codeOtr ${elem.field_type === DYNAMIC_FIELD_TYPES.MEMO ? "ck-editor-view" : ""}`}>
            {renderRichTextPopup()}
            <ReactTooltip id="dynamic_field" effect="solid" place="top" backgroundColor="#007bff" className="rc-tooltip-custom-class" />
            <div className="d-flex">
                <h4 className={elem.field_type === DYNAMIC_FIELD_TYPES.MEMO ? "ckeditor-band" : ""}>
                    {renderHighlightedLabel(elem.label, fieldSearch)}
                    {show_lease_chart && (
                        <img
                            data-for="dynamic_field"
                            data-tip="This field represents a chart event or milestone and will be displayed in lease-related charts and graphs. You can change this setting in the dynamic field settings."
                            className="ml-2 mb-1"
                            src={CalendarIcon}
                            height={15}
                            width={15}
                            alt=""
                            srcset=""
                        />
                    )}
                    {elem.field_type === DYNAMIC_FIELD_TYPES.MEMO && (
                        <button
                            class="butn-expansion"
                            data-for="input_field"
                            data-tip="Expand Field"
                            onClick={() => setShowRichTextPopup({ ...showRichTextPopup, show: true, label: elem.label, key: elem.label })}
                        >
                            <img src="/img/expand1.svg" alt="" />
                        </button>
                    )}
                </h4>
                {(isMR ? value && elem?.invalid : (elem?.field_type === "custom_select" && value && !elem?.uuid) || isInvalid) && (
                    <span className="new-span-badge new-hght-span">New</span>
                )}
            </div>
            <h3>
                {elem.field_type === DYNAMIC_FIELD_TYPES.NUMBER ? (
                    <NumberFormat
                        value={value || "-"}
                        decimalScale={elem.field_mode === "calculated" ? undefined : decimal_scale}
                        thousandSeparator={thousand_separator}
                        displayType={"text"}
                        prefix={prefix}
                        suffix={suffix}
                    />
                ) : elem.field_type === DYNAMIC_FIELD_TYPES.MEMO ? (
                    <>
                        {value ? (
                            <CKEditor
                                editor={Editor}
                                config={{ ...editorConfiguration, toolbar: [] }}
                                id="report-note-template"
                                data={value || ""}
                                onReady={editor => {
                                    editor?.enableReadOnlyMode("my-feature-id");
                                }}
                                // isReadOnly={true}
                            />
                        ) : (
                            ""
                        )}
                    </>
                ) : elem.field_type === DYNAMIC_FIELD_TYPES.CURRENCY ? (
                    <NumberFormat value={value || "-"} decimalScale={decimal_scale} thousandSeparator={true} displayType={"text"} prefix={"$ "} />
                ) : elem.field_type === DYNAMIC_FIELD_TYPES.DATE ? (
                    <>{value ? moment(value).format("MM-DD-YYYY") : "-"}</>
                ) : optionColor ? (
                    <span className="dynamic-field-color-pill" style={{ background: optionColor, color: readableTextColor(optionColor) }}>
                        {displayValue}
                    </span>
                ) : hasTooltip ? (
                    <EllipsisTooltipText className={`m-details ${dynamicClassName}`} text={displayValue || "-"} />
                ) : (
                    <>{displayValue || "-"}</>
                )}
            </h3>
        </div>
    );
};

export const Band = ({
    bandName = "",
    data = {},
    setToolTip,
    fields = [],
    bandId = "",
    handleBandClick,
    isDynamic,
    isBandOpen = false,
    invalidFields = {},
    hasTooltip = false,
    dynamicClassName = ""
}) => {
    let usedHeader = 0;
    const headerElems = [];
    let skipHeader = false;
    for (const elem of fields) {
        const origSpan = Number(elem.rules?.column_span) || 1;
        if (origSpan > 3) {
            skipHeader = true;
            usedHeader = 3;
            break;
        }
        const span = Math.min(origSpan, 3);
        if (usedHeader + span <= 3) {
            usedHeader += span;
            headerElems.push(elem);
        } else {
            break;
        }
    }
    const remainingHeaderCells = 3 - usedHeader;

    const bodyCandidates = fields.filter(elem => !headerElems.includes(elem));
    const bodyRows = [];
    let currentRow = [];
    let currentSpan = 0;

    bodyCandidates.forEach(elem => {
        const span = Math.min(Number(elem.rules?.column_span) || 1, 4);
        if (currentSpan + span > 4) {
            bodyRows.push(currentRow);
            currentRow = [];
            currentSpan = 0;
        }
        currentRow.push({ elem, span });
        currentSpan += span;
    });
    if (currentRow.length) bodyRows.push(currentRow);

    return (
        <div className="card">
            <div className="card-header" id={`heading-${bandId}`}>
                <div className="otr-recom-div">
                    <button
                        className="btn btn-link"
                        data-toggle="collapse"
                        onClick={handleBandClick}
                        data-target={`#${bandId}`}
                        aria-expanded={isBandOpen ? "true" : "false"}
                    >
                        {bandName}
                    </button>
                    {!skipHeader &&
                        headerElems.map(elem => {
                            const span = Math.min(Number(elem.rules?.column_span) || 1, 3);
                            const colSize = span * 3;
                            return (
                                <div key={elem.field_name} className={`col-md-${colSize} basic-box`}>
                                    {isDynamic ? (
                                        <DynamicField
                                            elem={elem}
                                            data={data}
                                            invalidFields={invalidFields}
                                            hasTooltip={hasTooltip}
                                            dynamicClassName={dynamicClassName}
                                        />
                                    ) : (
                                        <Field elem={elem} data={data} />
                                    )}
                                </div>
                            );
                        })}
                    {[...Array(remainingHeaderCells)].map((_, i) => (
                        <div key={`filler-${i}`} className="col-md-3 basic-box" />
                    ))}
                </div>
            </div>

            <div id={bandId} className={isBandOpen ? "collapse show" : "collapse"} aria-labelledby={`heading-${bandId}`}>
                <div className="card-body">
                    <div className="outer-rcm mt-1 basic-dtl-otr p-0 d-flex flex-wrap">
                        {bodyRows.map((row, rowIdx) => (
                            <React.Fragment key={rowIdx}>
                                {row.map(({ elem, span }) => (
                                    <div key={elem.field_name} className={`col-md-${span * 3} basic-box`}>
                                        {isDynamic ? (
                                            <DynamicField
                                                elem={elem}
                                                data={data}
                                                invalidFields={invalidFields}
                                                hasTooltip={hasTooltip}
                                                dynamicClassName={dynamicClassName}
                                            />
                                        ) : (
                                            <Field elem={elem} data={data} />
                                        )}
                                    </div>
                                ))}
                                {(() => {
                                    const used = row.reduce((sum, r) => sum + r.span, 0);
                                    const cols = (4 - used) * 3;
                                    return cols > 0 ? <div className={`col-md-${cols} basic-box`} /> : null;
                                })()}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
